import Storage from '@/handler/storageLocal'
import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import ConfigAPI from '@/config/api'

const APP_BUILD_VERSION_KEY = 'AppBuildVersion'
const APP_BUILD_RELOAD_KEY = 'AppBuildVersionReload'

async function clearServiceWorkerAndCaches() {
  try {
    if ('serviceWorker' in navigator) {
      // Desregistrar el SW invalida la PushSubscription del navegador (su
      // vida está atada a esta registration). Sin este reset, el flag de
      // "ya suscripto" quedaba en sessionStorage (sobrevive al reload) y la
      // re-suscripción automática de más abajo se salteaba creyendo que no
      // hacía falta — el usuario quedaba con las notificaciones push
      // apagadas después de cada actualización, sin darse cuenta.
      try {
        require('./handler/notifications.js').invalidatePushSubscriptionState()
      } catch (error) {}

      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch (error) {}

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    }
  } catch (error) {}
}

let deferredUpdateRetryPending = false

function checkAppBuildVersion() {
  fetch('/version.json?_=' + Date.now(), { cache: 'no-store' })
    .then((response) => (response.ok ? response.json() : null))
    .then(async (data) => {
      if (!data || !data.buildId) return

      const remoteBuildId = data.buildId.toString()
      const currentBuildId = Storage.getValue(APP_BUILD_VERSION_KEY)

      if (currentBuildId && currentBuildId !== remoteBuildId) {
        const reloadedBuildId = sessionStorage.getItem(APP_BUILD_RELOAD_KEY)
        if (reloadedBuildId !== remoteBuildId) {
          // Si una pantalla crítica está en medio de algo (pedido a medio
          // cargar, guardado o sincronización en curso), la recarga se
          // difiere: se avisa a la pantalla y se reintenta en 1 minuto.
          let busy = false
          try { busy = typeof window.__appBusy === 'function' && !!window.__appBusy() } catch (e) { busy = false }
          if (busy) {
            window.dispatchEvent(new CustomEvent('app-update-deferred'))
            if (!deferredUpdateRetryPending) {
              deferredUpdateRetryPending = true
              setTimeout(() => {
                deferredUpdateRetryPending = false
                checkAppBuildVersion()
              }, 60 * 1000)
            }
            return
          }

          sessionStorage.setItem(APP_BUILD_RELOAD_KEY, remoteBuildId)
          await clearServiceWorkerAndCaches()

          // El guard global de beforeunload (ModalProtectionMixin en app.vue)
          // bloquearía esta recarga con el diálogo "¿Salir del sitio?". Acá
          // ya se validó que la app no está ocupada, así que se suprime.
          window.__suppressBeforeUnload = true

          const currentUrl = new URL(window.location.href)
          currentUrl.searchParams.set('_v', remoteBuildId)
          window.location.replace(currentUrl.toString())
          return
        }
      }

      Storage.setValue(APP_BUILD_VERSION_KEY, remoteBuildId)
      if (sessionStorage.getItem(APP_BUILD_RELOAD_KEY) === remoteBuildId) {
        sessionStorage.removeItem(APP_BUILD_RELOAD_KEY)
      }
    })
    .catch(() => {})
}

window.__checkAppVersion = checkAppBuildVersion
checkAppBuildVersion()

// Re-chequear la versión durante la sesión: los vendedores dejan la PWA
// abierta días y el chequeo del arranque no alcanza para enterarse de un
// deploy. Si no hay conexión el fetch falla silenciosamente y no pasa nada.
const APP_VERSION_CHECK_INTERVAL = 10 * 60 * 1000
setInterval(checkAppBuildVersion, APP_VERSION_CHECK_INTERVAL)
window.addEventListener('online', checkAppBuildVersion)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') checkAppBuildVersion()
})

// Pedir almacenamiento persistente para que el navegador no evicte la sesión,
// el catálogo offline (IndexedDB) ni el cache de impresión. Crítico para abrir
// y facturar offline tras varios días sin conexión.
async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const already = await navigator.storage.persisted()
      if (!already) await navigator.storage.persist()
    }
  } catch (error) { /* API no disponible o denegada: se sigue sin persistencia garantizada */ }
}
requestPersistentStorage()

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js')
    .then((registration) => {
      console.log("Service Worker Registered!")

      // Forzar la búsqueda del service worker nuevo: una SPA con hash routing
      // no genera navegaciones, así que el navegador puede tardar hasta 24h en
      // revisar /service-worker.js por su cuenta. El SW nuevo hace
      // skipWaiting + claim, así que apenas se descarga toma control.
      const swUpdate = () => { registration.update().catch(() => {}) }
      setInterval(swUpdate, APP_VERSION_CHECK_INTERVAL)
      window.addEventListener('online', swUpdate)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') swUpdate()
      })

      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'SW_ACTIVATED') {
          window.dispatchEvent(new CustomEvent('sw-activated'))
        }
      })

      try {
        var cachedConfig = null
        try { cachedConfig = JSON.parse(Storage.getValue('ConfigurationNotificationsPush')) } catch(e) { cachedConfig = null }

        if (cachedConfig && cachedConfig.vapidPublicKey) {
          require("./handler/notifications.js").autoSubscribeIfNeeded()
        } else {
          Vue.use(VueAxios, axios)
          const instance = axios.create()
          instance.get(ConfigAPI.baseURL + "public/notifications/push/1/configuration")
            .then((res) => {
              Storage.setValue('ConfigurationNotificationsPush', JSON.stringify(res.data))
              try { require("./handler/notifications.js").autoSubscribeIfNeeded() } catch(e) { /* push no disponible */ }
            })
            .catch(() => {})
        }
      } catch(e) {
        // Push no disponible (sin conexión o el navegador no lo soporta)
      }
    })
    .catch(err => console.warn("Service Worker no disponible:", err))
}

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import 'core-js/es6/promise'
import 'core-js/es6/string'
import 'core-js/es7/array'
// import cssVars from 'css-vars-ponyfill'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import App from './app'
import router from './router'
import VueAWN from "vue-awesome-notifications"
import Loading from 'vue-loading-overlay';
import moment from 'moment'
import excel from 'vue-excel-export'
import VueLazyload from 'vue-lazyload'
import Session from '@/handler/session'
import vSelect from 'vue-select'
import AppTextarea from '@/components/inc/textarea/AppTextarea'

// Select2
Vue.component('v-select', vSelect)
// End select2

// Reemplazo de b-form-textarea (ver comentario en el propio componente)
Vue.component('app-textarea', AppTextarea)
 
// todo
// cssVars()

// Color Principal
var primaryColor = 'black'
if(Session.getSession().settings) {
  primaryColor = Session.getSession().settings.color_primary
}
// End color Principal

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons)
Vue.use(VueAWN,{
  maxNotifications: 1
});

Vue.use(Loading,{
    //container: this.$refs.loadingContainer,
    color: primaryColor,
    loader: 'spinner',
    width: 128,
    height: 128,
    backgroundColor: '#ffffff',
    opacity: 0.3,
})
Vue.use(excel)
Vue.use(VueLazyload)

moment.locale('es')
Vue.prototype.moment = moment

/* eslint-disable no-new */
var VueApp =  new Vue({
  el: '#app',
  router,
  template: '<App/>',
  components: {
    App
  }
})
